import React from 'react';
import useFetch, { CachePolicies } from 'use-http';
import Paginate from 'components/UI/Paginate';
import usePaginate from 'hooks/usePaginate';
import Table from 'components/UI/Table';
import { BreachEnum, IBreach } from 'types/breach';
import Select from 'components/UI/Select';
import 'react-datepicker/dist/react-datepicker.css';
import { useUnit } from 'effector-react';
import { $store } from 'models/store';
import { $user } from 'models/user';
import { getSortImage } from './utils';
import { PER_PAGE, BREACH_TYPES, BREACH_OPTIONS } from './common/constants';
import { createSortHandler } from './common/tableUtils';
import { useStoresAndUsers } from './common/hooks/useStoresAndUsers';
import { useBreachData } from './common/hooks/useBreachData';
import { useFilterInitialization } from './common/hooks/useFilterInitialization';
import { useFilterPagination } from './common/hooks/useFilterPagination';
import {
	createStoreNumberColumn,
	createStoreTitleColumn,
	createPositionColumn,
	createNameColumn,
	createRegionalDirectorColumn,
	createAoManagerColumn,
} from './common/tableColumns';
import {
	createDateFilterFunction,
	createTimeFormatter,
	createActionFormatter,
} from './common/formatters';
import { createSessionsBrokenExcelExport } from './common/excelExportUtils';

export default function SessionsBroken() {
	const store = useUnit($store);
	const user = useUnit($user);
	const { error, loading } = useFetch({ cachePolicy: CachePolicies.NO_CACHE });

	const { filter, setFilter } = useFilterInitialization(user);
	const { pageCount, offset, setCount, handlePageClick, resetOffset, currentPage } = usePaginate(PER_PAGE);

	const { userList, isLoadingUsers, storeIdOptions, regionalManagersOptions, aoManagersOptions, isFilterLocked, loadUser } =
		useStoresAndUsers(filter);

	const { data, load } = useBreachData(filter, offset);

	const sortTable = createSortHandler(filter, setFilter);

	useFilterPagination(filter, resetOffset);

	React.useEffect(() => {
		load().then(result => {
			if (result) {
				setCount(result.count);
			}
		});
	}, [offset, filter, store, load]);

	const headers = [
		createStoreNumberColumn(filter, setFilter, storeIdOptions, isFilterLocked, sortTable),
		createStoreTitleColumn(filter, sortTable),
		createPositionColumn(filter, sortTable),
		createNameColumn(filter, setFilter, userList, loadUser, isFilterLocked, sortTable, isLoadingUsers),
		{
			value: 'type',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Тип нарушения</span>
					<button onClick={() => sortTable('type')}>
						{getSortImage('type', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			width: '500px',
			formatted: React.useCallback((row: IBreach) => {
				return (
					<span>
						{(row.type === BreachEnum.DiffToken ||
							row.type === BreachEnum.EndSession ||
							row.type === BreachEnum.RegManual) && <>{BREACH_TYPES[row.type]}</>}
						{row.type === BreachEnum.AnotherShift && (
							<>
								{BREACH_TYPES[row.type]}
								<span> ({row.data?.total} раз) </span>
							</>
						)}
						{row.type === BreachEnum.DiffGeo && row.data?.diffGeo && (
							<>
								{row?.checkin?.lat && (
									<a
										href={`https://geotree.ru/coordinates?lat=${row.checkin.lat}&lon=${row.checkin.lon}&z=18&mlat=${row.checkin.lat}&mlon=${row.checkin.lon}`}
										target="_black"
									>
										{BREACH_TYPES[row.type]}
									</a>
								)}
								<span>({(+(1000 * +row.data?.diffGeo).toFixed(0)).toLocaleString('ru')}) м</span>
							</>
						)}
						{(row.type === BreachEnum.ErrorGeo || row.type === BreachEnum.PhotoError) && (
							<>
								{BREACH_TYPES[row.type]}
								<div>
									{row.data?.uri && (
										<a href={row.data?.uri} target="_blank" rel="noreferrer">
											Посмотреть селфи
										</a>
									)}
								</div>
							</>
						)}
					</span>
				);
			}, []),
			filter: React.useCallback(() => {
				const handleChange = (value: string) => {
					setFilter({ ...filter, type: value });
				};
				return <Select onChange={handleChange} options={BREACH_OPTIONS} isClearable defaultValue={filter.type} />;
			}, [filter, setFilter]),
		},
		{
			value: 'open',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Действие</span>
					<button onClick={() => sortTable('open')}>
						{getSortImage('open', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			formatted: createActionFormatter(),
		},
		{
			value: 'time',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Время в магазине</span>
					<button onClick={() => sortTable('createdAt')}>
						{getSortImage('createdAt', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			width: '300px',
			formatted: createTimeFormatter(false),
			filter: createDateFilterFunction(filter, setFilter),
		},
		{
			value: 'time_to_msk',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Время в магазине по МСК</span>
				</div>
			),
			width: '300px',
			formatted: createTimeFormatter(true),
			filter: createSessionsBrokenExcelExport(filter, user, store),
		},
		createRegionalDirectorColumn(filter, setFilter, regionalManagersOptions, sortTable, user?.work_place),
		createAoManagerColumn(filter, setFilter, aoManagersOptions, sortTable, user?.work_place),
	];

	return (
		<div>
		{error && 'Error!'}
		<Table headers={headers} rows={data} isLoading={loading} />
		{pageCount > 1 && <Paginate handlePageClick={handlePageClick} pageCount={pageCount} forcePage={currentPage} />}
		</div>
	);
}
